import {
  Clock,
  Files,
  Users,
  Folder,
  FolderOpen,
  HardDrive,
  Star,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { formatBytes } from '../../../../lib/formatBytes';
import { repositoryApi } from '../repositoryApi';
import type { RepositoryFolderOption, RepositoryStorage } from '../repositoryTypes';

/**
 * Which listing the repository is showing. Exactly one at a time.
 *
 * A UNION rather than four booleans, and that is the whole reason it reads cleanly: the scopes
 * are mutually exclusive — a reader is looking at their pins or at what is expired, never both
 * — and four flags would make every impossible combination expressible and leave the page
 * deciding which wins.
 */
export type RepositoryScope =
  | 'folder'
  | 'mine'
  | 'shared'
  | 'all'
  | 'pinned'
  | 'expired'
  | 'trash';

/**
 * Which scopes list FILES ONLY, with no folders among them.
 *
 * **Three of them are about documents, not about the tree.** "All files" says so in its own
 * name; Expired is a retention question, and retention is stamped on controlled documents
 * alone; Pinned is the exception in the other direction and deliberately keeps its folders,
 * because a pinned folder is a shortcut a reader put there on purpose.
 *
 * Exported so the page and the rail cannot disagree about which listing is which — the page
 * sends the type filter and the rail draws the row, and a second copy of this set is how those
 * two come to mean different things.
 */
export const FILES_ONLY_SCOPES: ReadonlySet<RepositoryScope> = new Set(['all', 'expired']);

/**
 * The Document Management rail — a file browser's navigation, not a facet filter.
 *
 * **A DEDICATED component, and the standard `<IndexFilterRail>` is untouched.** Product call,
 * 18 Sep 2026. A repository's left column answers "where am I and what am I looking at", which
 * is navigation; a facet rail answers "narrow what is in front of me", which is filtering.
 * Bending the shared rail to also draw folders would put one module's navigation into the
 * component every other module depends on — so `<IndexPage>` takes a `rail` slot and this
 * replaces it.
 *
 * **Captured from GRC's Documents workspace**, which is where the shape comes from: a storage
 * bar, a run of views, and a pinned section.
 *
 * **"My documents" is a VIEW here, not the reference's per-user root.** GRC's is a home a
 * reader navigates into; this narrows the listing to what the caller created and changes
 * nothing about who may read what. That distinction is the whole reason the row is acceptable:
 * a per-user root would be inventing a concept this product does not have, where a filter on
 * `CreatedBy` states a fact the row already carries.
 */
export function RepositoryNavRail({
  scope,
  folderId,
  onScopeChange,
  onOpenFolder,
  pinned,
  reloadKey,
}: {
  scope: RepositoryScope;
  /** The folder being browsed, so a root row can show as selected. */
  folderId: string | null;
  onScopeChange: (scope: RepositoryScope) => void;
  onOpenFolder: (id: string | null) => void;
  /** This reader's pinned items, for the Pinned section. Supplied by the page. */
  pinned: { id: string; name: string; isFolder: boolean }[];
  /** Bumped by the page when something is written, so the rail re-reads what it derives. */
  reloadKey: number;
}) {
  const canRead = usePermission('repository.read');

  const [storage, setStorage] = useState<RepositoryStorage | null>(null);
  const [roots, setRoots] = useState<RepositoryFolderOption[]>([]);

  /*
    Both reads fail SILENTLY. The rail is chrome over a listing that stands on its own: a
    storage figure that would not load must not take the repository down with it, and a folder
    list that did not arrive leaves the rail shorter rather than broken. The breadcrumb is
    still how a reader navigates, and it comes with the page.
  */
  useEffect(() => {
    if (!canRead) return;

    const controller = new AbortController();

    repositoryApi
      .storage(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setStorage(next);
      })
      .catch(() => undefined);

    repositoryApi
      .folderOptions(undefined, controller.signal)
      .then((options) => {
        if (controller.signal.aborted) return;

        /*
          ROOTS only. `path` is the breadcrumb joined with " › ", so a folder at the root has a
          path equal to its own name — no separator in it. Derived rather than asking the server
          for a `parentId` it does not send, and it is exact rather than a heuristic: every
          deeper folder has at least one separator by construction.
        */
        setRoots(options.filter((option) => !option.path.includes('›')));
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [canRead, reloadKey]);

  const usedLabel = storage ? formatBytes(storage.usedBytes) : null;

  /*
    Zero total means the volume could not be read, which is rendered as the used figure alone
    rather than as "0 B of 0 B" — a full disk is a different claim from an unknown one, and the
    bar is suppressed because a percentage of zero has no honest value.
  */
  const hasCapacity = Boolean(storage && storage.totalBytes > 0);

  const usedPercent =
    storage && hasCapacity
      ? Math.min(100, (storage.usedBytes / storage.totalBytes) * 100)
      : 0;

  return (
    <aside className="modern-scrollbar flex w-64 shrink-0 flex-col overflow-y-auto border-r border-slate-200 bg-white">
      {/* ── Storage ─────────────────────────────────────────────────────────── */}
      {storage ? (
        <div className="border-b border-slate-100 px-4 py-3">
          <p className="flex items-center gap-1.5 text-[9px] font-normal uppercase tracking-wider text-gray-600">
            <HardDrive className="h-3 w-3" aria-hidden="true" />
            Storage
          </p>

          {hasCapacity ? (
            <div
              className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100"
              role="progressbar"
              aria-valuenow={Math.round(usedPercent)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${usedLabel} of ${formatBytes(storage.totalBytes)} used`}
            >
              <div
                className="h-full rounded-full bg-brand-500 transition-[width]"
                style={{ width: `${usedPercent}%` }}
              />
            </div>
          ) : null}

          <p className="mt-1.5 font-mono text-xs tabular-nums text-slate-500">
            {hasCapacity
              ? `${usedLabel} of ${formatBytes(storage.totalBytes)}`
              : `${usedLabel} stored`}
          </p>
        </div>
      ) : null}

      {/* ── The views ───────────────────────────────────────────────────────── */}
      <nav className="flex flex-col gap-0.5 px-2 py-2">
        {/*
          MY DOCUMENTS leads, because it is the narrowest view and the one a reader returns to.

          It is what this caller CREATED — a view, not a per-user home. There is no per-user
          root in this product, so the row narrows a listing rather than opening a place, and
          everything in it is visible to every other reader exactly as it was.
        */}
        <RailRow
          icon={<FolderOpen className="h-4 w-4" aria-hidden="true" />}
          label="My documents"
          selected={scope === 'mine'}
          onClick={() => onScopeChange('mine')}
        />
        {/*
          SHARED WITH ME lists the GRANTS somebody made to this reader, not everything they can
          see. A public folder is readable by everyone and belongs to nobody here; what they
          created is My documents, one row up.

          It sits directly under My documents because the pair answers one question between
          them — what is mine, and what was given to me.
        */}
        <RailRow
          icon={<Users className="h-4 w-4" aria-hidden="true" />}
          label="Shared with me"
          selected={scope === 'shared'}
          onClick={() => onScopeChange('shared')}
        />
        <RailRow
          icon={<Files className="h-4 w-4" aria-hidden="true" />}
          label="All files"
          selected={scope === 'all'}
          onClick={() => onScopeChange('all')}
        />
        <RailRow
          icon={<Star className="h-4 w-4" aria-hidden="true" />}
          label="Pinned"
          selected={scope === 'pinned'}
          onClick={() => onScopeChange('pinned')}
        />
        <RailRow
          icon={<Clock className="h-4 w-4" aria-hidden="true" />}
          label="Expired"
          selected={scope === 'expired'}
          onClick={() => onScopeChange('expired')}
        />
        <RailRow
          icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
          label="Trash"
          selected={scope === 'trash'}
          onClick={() => onScopeChange('trash')}
        />
      </nav>

      {/* ── Root folders ────────────────────────────────────────────────────── */}
      {roots.length > 0 ? (
        <div className="border-t border-slate-100 pb-2 pt-3">
          <p className="px-4 pb-1 text-[9px] font-normal uppercase tracking-wider text-gray-600">
            Folders
          </p>

          <div className="flex flex-col gap-0.5 px-2">
            {roots.map((root) => (
              <RailRow
                key={root.id}
                icon={<Folder className="h-4 w-4" aria-hidden="true" />}
                label={root.name}
                /*
                  Selected only while BROWSING it — a root is not selected because a document
                  three folders inside it is on screen, which would leave two rows of the rail
                  claiming to be where the reader is.
                */
                selected={scope === 'folder' && folderId === root.id}
                onClick={() => onOpenFolder(root.id)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* ── Pinned ──────────────────────────────────────────────────────────── */}
      {pinned.length > 0 ? (
        <div className="border-t border-slate-100 pb-3 pt-3">
          <p className="px-4 pb-1 text-[9px] font-normal uppercase tracking-wider text-gray-600">
            Pinned
          </p>

          <div className="flex flex-col gap-0.5 px-2">
            {pinned.map((item) => (
              <RailRow
                key={item.id}
                /*
                  A pinned FOLDER opens; a pinned FILE has no listing to open, so the rail lists
                  it and the reader reaches it through the Pinned view. Drawing a row that does
                  nothing would be the dead affordance the hide-rule forbids, so a file's row
                  opens the Pinned listing — where its own anchor takes it to its profile.
                */
                icon={
                  item.isFolder ? (
                    <Folder className="h-4 w-4 text-brand-500" aria-hidden="true" />
                  ) : (
                    <Files className="h-4 w-4" aria-hidden="true" />
                  )
                }
                label={item.name}
                selected={scope === 'folder' && folderId === item.id}
                onClick={() =>
                  item.isFolder ? onOpenFolder(item.id) : onScopeChange('pinned')
                }
              />
            ))}
          </div>
        </div>
      ) : null}
    </aside>
  );
}

/**
 * One row of the rail.
 *
 * **The selected row is the darkest text in the column and carries a tint** — the product's
 * selection treatment, and NOT `font-medium`: a rail is a column of short labels read top to
 * bottom, and one bolded row in it reads as a heading over the rows beneath rather than as the
 * row you are on. That is the same call `<ProfileNav>` takes.
 */
function RailRow({
  icon,
  label,
  selected,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={selected ? 'true' : undefined}
      className={[
        'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition',
        selected ? 'bg-blue-50 text-slate-900' : 'text-slate-600 hover:bg-slate-100',
      ].join(' ')}
    >
      <span className={selected ? 'text-slate-900' : 'text-slate-400'}>{icon}</span>
      <span className="truncate" title={label}>
        {label}
      </span>
    </button>
  );
}
